import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export default class SkillsAnimations {
  constructor() {
    gsap.registerPlugin(ScrollTrigger);
    this.init();
  }

  init() {
    this.setupLetterSpans();
    this.setupScrollAnimations();
    this.setupTechBackground();
  }

  setupLetterSpans() {
    const skillTitles = document.querySelectorAll('.skill-title');
    
    skillTitles.forEach(title => {
      const text = title.textContent;
      title.innerHTML = '';
      
      const letterSpan = document.createElement('span');
      letterSpan.className = 'skill-letters';
      
      [...text].forEach(char => {
        const span = document.createElement('span');
        span.textContent = char === ' ' ? '\u00A0' : char;
        // Start letters positioned below (ready to roll up)
        gsap.set(span, { y: '120%', opacity: 0 });
        letterSpan.appendChild(span);
      });
      
      title.appendChild(letterSpan);
    });
  }

  setupScrollAnimations() {
    // Get all skill titles
    const skillTitles = gsap.utils.toArray('.skill-title');

    if (skillTitles.length === 0) return;

    // Main ScrollTrigger for skills section
    ScrollTrigger.create({
      trigger: "#skills",
      start: "top 50%", // Skills animation trigger
      end: "bottom top", // End when skills section bottom reaches viewport top (aligns with pin end)
      onEnter: () => {
        // Scrolling down - animate skills in (top to bottom order)
        this.animateSkillsIn(skillTitles, "start");
      },
      onLeave: () => {
        // Scrolling down past skills - animate skills out (bottom to top order)
        this.animateSkillsOut(skillTitles, "end");
      },
      onEnterBack: () => {
        // Scrolling up back into skills - animate skills in (bottom to top order)
        this.animateSkillsIn(skillTitles, "end");
      },
      onLeaveBack: () => {
        // Scrolling up past skills - animate skills out (top to bottom order)
        this.animateSkillsOut(skillTitles, "start");
      }
    });
  }

  animateSkillsIn(skillTitles, staggerFrom = "start") {
    // First, animate SVG connection lines from center outward
    const connectionSvg = document.querySelector('.skills-connection-svg');
    const connectionLines = document.querySelectorAll('.connection-line');
    
    // Show SVG container (no animation)
    connectionSvg.style.opacity = '1';
    
    // Animate connection lines drawing outward from Mii head
    gsap.to(connectionLines, {
      strokeDashoffset: 0,
      duration: 1.2,
      stagger: { each: 0.15, from: staggerFrom },
      ease: "power1.out"
    });
    
    // Then animate skill titles with a small delay
    gsap.delayedCall(0.4, () => {
      skillTitles.forEach((title, titleIndex) => {
        const letters = title.querySelectorAll('.skill-letters span');
        
        gsap.delayedCall(titleIndex * 0.15, () => {
          // Add border animation
          title.classList.remove('animate-out');
          title.classList.add('animate-in');
          
          // Animate letters rolling up from below
          gsap.to(letters, {
            y: '0%',
            opacity: 1,
            duration: 0.28,
            stagger: 0.035,
            ease: "cubic-bezier(0.77,0,0.175,1)"
          });
        });
      });
    });
  }

  animateSkillsOut(skillTitles, staggerFrom = "end") {
    const connectionSvg = document.querySelector('.skills-connection-svg');
    const connectionLines = document.querySelectorAll('.connection-line');
    
    skillTitles.forEach((title, titleIndex) => {
      const letters = title.querySelectorAll('.skill-letters span');

      gsap.delayedCall(titleIndex * 0.1, () => {
        // Animate letters rolling down
        gsap.to(letters, {
          y: '120%',
          opacity: 0,
          duration: 0.28,
          stagger: { each: 0.035, from: "end" },
          ease: "cubic-bezier(0.77,0,0.175,1)"
        });

        // Remove border animation
        gsap.delayedCall(0.2, () => {
          title.classList.remove('animate-in');
          title.classList.add('animate-out');
        });
      });
    });
    
    // Animate connection lines retracting back to skill categories
    gsap.delayedCall(0.3, () => {
      gsap.to(connectionLines, {
        strokeDashoffset: 111,
        duration: 1.2,
        stagger: { each: 0.15, from: staggerFrom },
        ease: "power1.in"
      });
    });
  }

  setupTechBackground() {
    const backgroundText = document.getElementById('techBackgroundText');
    const skillIcons = document.querySelectorAll('.skill-icon');
    
    // Store current animation timeline
    this.currentTechAnimation = null;
    
    skillIcons.forEach(icon => {
      const techName = icon.getAttribute('alt');
      
      icon.addEventListener('mouseenter', () => {
        this.showTechBackground(backgroundText, techName);
      });
      
      icon.addEventListener('mouseleave', () => {
        this.hideTechBackground(backgroundText);
      });
    });
  }
  
  showTechBackground(element, techName) {
    // Kill any existing animation first
    if (this.currentTechAnimation) {
      this.currentTechAnimation.kill();
    }
    
    // Clear existing content
    element.innerHTML = '';
    element.classList.remove('animate-out');
    
    // Create letter spans like skill titles
    const letterSpan = document.createElement('span');
    letterSpan.className = 'tech-letters';
    
    [...techName].forEach((char, index) => {
      const span = document.createElement('span');
      span.textContent = char === ' ' ? '\u00A0' : char;
      // Start letters positioned below (ready to roll up)
      gsap.set(span, { y: '120%', opacity: 0 });
      letterSpan.appendChild(span);
    });
    
    element.appendChild(letterSpan);
    element.classList.add('show');
    
    // Animate letters rolling up with stagger
    const letters = letterSpan.querySelectorAll('span');
    this.currentTechAnimation = gsap.to(letters, {
      y: '0%',
      opacity: 1,
      duration: 0.28,
      ease: "cubic-bezier(0.77,0,0.175,1)",
      stagger: 0.035
    });
  }
  
  hideTechBackground(element) {
    // Kill any existing animation first
    if (this.currentTechAnimation) {
      this.currentTechAnimation.kill();
    }
    
    const letters = element.querySelectorAll('.tech-letters span');
    if (letters.length > 0) {
      this.currentTechAnimation = gsap.to(letters, {
        y: '120%',
        opacity: 0,
        duration: 0.28,
        ease: "cubic-bezier(0.77,0,0.175,1)",
        stagger: { each: 0.035, from: "end" },
        onComplete: () => {
          element.innerHTML = '';
          element.classList.remove('show');
          this.currentTechAnimation = null;
        }
      });
    } else {
      element.classList.remove('show');
    }
  }

  // Method to refresh animations
  refresh() {
    ScrollTrigger.refresh();
  }

  // Cleanup method
  destroy() {
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.vars.trigger && trigger.vars.trigger.id === 'skills') {
        trigger.kill();
      }
    });
  }
}
